/**
 * Verbatim port of raven-core/src/encoding/analyzer.ts (Sprint 17.5 expanded).
 * Cross-platform parity with iOS depends on this enumeration matching exactly.
 */

import type { TextAnalysis, WordInfo } from './types.js';

const PUNCTUATION_CHARS = new Set([
  '.', ',', ';', ':', '!', '?', "'", '"', '-',
  '(', ')', '[', ']', '{', '}', '/',
  '@', '#', '$', '%', '^', '&', '*', '_', '+', '=', '<', '>', '~',
]);

const VOWELS = new Set(['a', 'e', 'i', 'o', 'u']);

function isAsciiLetter(ch: string): boolean {
  return (ch >= 'a' && ch <= 'z') || (ch >= 'A' && ch <= 'Z');
}

function isAsciiLowerLetter(ch: string): boolean {
  return ch >= 'a' && ch <= 'z';
}

function trimToLetters(token: string): string {
  let i = 0;
  let j = token.length;
  while (i < j && !isAsciiLetter(token[i] ?? '')) i++;
  while (j > i && !isAsciiLetter(token[j - 1] ?? '')) j--;
  return token.slice(i, j);
}

function countEmoji(text: string): number {
  if (typeof Intl === 'undefined' || typeof (Intl as unknown as { Segmenter?: unknown }).Segmenter !== 'function') {
    return 0;
  }
  const SegmenterCtor = (Intl as unknown as { Segmenter: new (...args: unknown[]) => { segment(s: string): Iterable<{ segment: string }> } }).Segmenter;
  const seg = new SegmenterCtor('en', { granularity: 'grapheme' });
  const re = /\p{Extended_Pictographic}/u;
  let count = 0;
  for (const { segment } of seg.segment(text)) {
    if (re.test(segment)) count++;
  }
  return count;
}

function countDoubleLetterRuns(lower: string): number {
  let runs = 0;
  let i = 0;
  while (i < lower.length) {
    const ch = lower[i] ?? '';
    if (isAsciiLowerLetter(ch) && lower[i + 1] === ch) {
      runs++;
      while (i < lower.length && lower[i] === ch) i++;
    } else {
      i++;
    }
  }
  return runs;
}

export function analyzeText(text: string): TextAnalysis {
  const lower = text.toLowerCase();

  let eCount = 0;
  let aCount = 0;
  let iCount = 0;
  let oCount = 0;
  let uCount = 0;
  let consonantCount = 0;
  let uppercaseCount = 0;
  let digitCount = 0;
  let punctuationCount = 0;

  for (let j = 0; j < text.length; j++) {
    const original = text[j] ?? '';
    const ch = lower[j] ?? '';
    if (ch >= 'a' && ch <= 'z') {
      if (ch === 'e') eCount++;
      else if (ch === 'a') aCount++;
      else if (ch === 'i') iCount++;
      else if (ch === 'o') oCount++;
      else if (ch === 'u') uCount++;
      else consonantCount++;
      if (original >= 'A' && original <= 'Z') uppercaseCount++;
    } else if (ch >= '0' && ch <= '9') {
      digitCount++;
    } else if (PUNCTUATION_CHARS.has(ch)) {
      punctuationCount++;
    }
  }

  const tokens = lower.split(/\s+/).filter((w) => w.length > 0);
  const words: WordInfo[] = tokens.map((w, idx) => ({
    word: w,
    length: w.replace(/[^a-z]/g, '').length,
    position: idx + 1,
  }));

  let longestWordLength = 0;
  let shortestWordLength = 0;
  let wordsStartingWithVowel = 0;
  let wordsEndingWithVowel = 0;
  if (words.length > 0) {
    const first = words[0];
    if (first !== undefined) {
      longestWordLength = first.length;
      shortestWordLength = first.length;
    }
    for (const w of words) {
      if (w.length > longestWordLength) longestWordLength = w.length;
      if (w.length < shortestWordLength) shortestWordLength = w.length;
      const trimmed = trimToLetters(w.word);
      if (trimmed.length > 0) {
        const head = trimmed[0] ?? '';
        const tail = trimmed[trimmed.length - 1] ?? '';
        if (VOWELS.has(head)) wordsStartingWithVowel++;
        if (VOWELS.has(tail)) wordsEndingWithVowel++;
      }
    }
  }

  let codepointCount = 0;
  for (const _ of text) codepointCount++;

  return {
    totalVowels: eCount + aCount + iCount + oCount + uCount,
    eCount,
    aCount,
    iCount,
    oCount,
    uCount,
    words,
    charCount: codepointCount,
    consonantCount,
    longestWordLength,
    shortestWordLength,
    wordsStartingWithVowel,
    wordsEndingWithVowel,
    uppercaseCount,
    digitCount,
    punctuationCount,
    emojiCount: countEmoji(text),
    doubleLetterCount: countDoubleLetterRuns(lower),
  };
}
